import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useNavigationType } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ChevronLeft, ChevronRight, HelpCircle, Loader2, Plus, RefreshCw, Search, SearchX, ShieldAlert, Trophy } from 'lucide-react'
import toast from 'react-hot-toast'
import HelpModal from '../../../components/HelpModal'
import { useTeamStore } from '../store/useTeamStore'
import TeamCatalogCard from '../components/TeamCatalogCard'
import TeamCreateModal from '../components/TeamCreateModal'
import TeamApplyModal from '../components/TeamApplyModal'
import MyApplicationsList from '../components/MyApplicationsList'
import TeamAvatar from '../components/TeamAvatar'
import TeamCatalogFilters from '../components/TeamCatalogFilters'
import RecentLeftTeams from '../components/RecentLeftTeams'
import { CATALOG_DEFAULTS } from '../utils/teamCatalogFilters'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact } from '../../../utils/haptic'

const SEARCH_MIN = 2
const CATALOG_RETURN_KEY = 'teamCatalogReturn'

const readCatalogReturn = () => {
  try {
    const value = sessionStorage.getItem(CATALOG_RETURN_KEY)
    return value ? JSON.parse(value) : null
  } catch {
    sessionStorage.removeItem(CATALOG_RETURN_KEY)
    return null
  }
}

/**
 * Каталог банд (`/teams`).
 * Без поиска бэкенд отдаёт только набирающие банды, отсортированные по числу
 * игроков онлайн; с поиском (от 2 символов) — все живые, поэтому в выдаче
 * встречаются карточки с `recruiting: false`.
 *
 * Фильтры (docs/team-api.md §2) работают и вместе с поиском. Стартовое
 * состояние — любая активность и `free=1`: заполненные банды скрыты.
 * Смена любого фильтра сбрасывает страницу на первую.
 */
const Teams = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const { playSound } = useAudioManager({ pick: 'pick', select: 'select' })
  const user = useAppStore((state) => state.user)
  const isGuest = useAppStore((state) => state.isGuest)
  const minesEnabled = useAppStore((state) => state.user?.mines_enabled !== false)
  const openLogin = useAppStore((state) => state.openLogin)
  const {
    catalog, isCatalogLoading, catalogError, top, capture, captureError,
    myApplications, loadCatalog, loadMyApplications, loadTop, loadCapture
  } = useTeamStore()

  const [returnState] = useState(() => {
    if (navigationType === 'POP') return readCatalogReturn()
    sessionStorage.removeItem(CATALOG_RETURN_KEY)
    return null
  })
  const [search, setSearch] = useState(returnState?.search ?? '')
  const [query, setQuery] = useState(returnState?.query ?? '')
  const [page, setPage] = useState(returnState?.page ?? 1)
  const [filters, setFilters] = useState(() => ({ ...CATALOG_DEFAULTS, ...returnState?.filters }))
  useEffect(() => {
    if (!minesEnabled) setFilters((current) => current.modes.includes('mines') ? { ...current, modes: current.modes.filter((mode) => mode !== 'mines') } : current)
  }, [minesEnabled])
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [applyTeam, setApplyTeam] = useState(null)
  const previousSearchRef = useRef(search)
  const restoredScrollRef = useRef(!returnState)

  const myTeam = user?.team ?? null
  const canCreate = user?.can_create_team === true

  // Банды с активной заявкой: в карточке кнопку меняем на неактивную «Заявка отправлена»
  const pendingTeamIds = new Set(
    myApplications.filter((app) => app.status === 'pending').map((app) => app.team?.id)
  )

  // Поиск с debounce; запрос от 2 символов, иначе бэкенд вернёт query_too_short
  useEffect(() => {
    const searchChanged = previousSearchRef.current !== search
    previousSearchRef.current = search
    const timer = setTimeout(() => {
      const value = search.trim()
      setQuery(value.length >= SEARCH_MIN ? value : '')
      if (searchChanged) setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    let cancelled = false
    let animationFrame

    const load = async () => {
      await loadCatalog({ q: query, page, ...filters, modes: minesEnabled ? filters.modes : filters.modes.filter((mode) => mode !== 'mines') })
      if (cancelled || restoredScrollRef.current) return

      restoredScrollRef.current = true
      animationFrame = requestAnimationFrame(() => {
        window.scrollTo(0, Number(returnState?.scrollY) || 0)
        sessionStorage.removeItem(CATALOG_RETURN_KEY)
      })
    }

    load()
    return () => {
      cancelled = true
      if (animationFrame) cancelAnimationFrame(animationFrame)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, filters, isGuest])

  useEffect(() => {
    loadTop()
    loadCapture()
    const timer = setInterval(() => { if (document.visibilityState === 'visible') loadCapture() }, 25000)
    const onVisible = () => { if (document.visibilityState === 'visible') loadCapture() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
  }, [loadTop, loadCapture, isGuest])

  useEffect(() => {
    if (!myTeam) return undefined
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') useAppStore.getState().loadProfile().catch(() => {})
    }, 20000)
    return () => clearInterval(timer)
  }, [myTeam])

  /**
   * Любая смена фильтра возвращает выдачу на первую страницу. Повторный тап по
   * уже выбранному чипу ничего не меняет — лишний запрос каталога не нужен.
   */
  const changeFilters = (patch) => {
    const isSame = Object.keys(patch).every((key) => patch[key] === filters[key])
    if (isSame) return
    playSound('pick')
    setPage(1)
    setFilters((prev) => ({ ...prev, ...patch }))
  }

  // Свои заявки нужны только тем, кто пока не в банде
  useEffect(() => {
    if (isGuest) useTeamStore.setState({ myApplications: [] })
    else if (!myTeam) loadMyApplications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTeam, isGuest])

  const handleOpenTeam = (team) => {
    playSound('select')
    sessionStorage.setItem(CATALOG_RETURN_KEY, JSON.stringify({
      scrollY: window.scrollY,
      search,
      query,
      page,
      filters
    }))
    navigate(`/teams/${team.id}`)
  }

  const handleCreateClick = () => {
    if (isGuest) return openLogin()
    if (myTeam) return
    if (!canCreate) {
      // can_create_team: false без причины — нет пополнений, часовой кулдаун
      // после роспуска по таймеру либо исчерпан суточный лимит на 3 банды
      toast(t('team.createUnavailable', 'Создать банду можно после пополнения баланса. Если ты только что распустил свою — подожди час. За сутки можно создать не больше 3 банд.'), { duration: 5000 })
      return
    }
    hapticImpact('medium')
    setIsCreateOpen(true)
  }

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-6xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-6">
        <div className="min-w-0 space-y-5">
        {/* Заголовок: на мобильных — картинка во всю ширину экрана, прижата к верху */}
        <motion.div variants={SECTION_VARIANT} className="-mx-4 -mt-6 overflow-hidden bg-surface shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] lg:mx-0 lg:mt-0 lg:rounded-xl">
          <img
            src="/images/teams-banner.jpg"
            alt={t('team.title', 'Банды')}
            className="aspect-[16/9] w-full object-cover"
            loading="lazy"
          />
          <div className="p-4 lg:p-5">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl text-white">{t('team.title', 'Банды')}</h1>
              <button
                type="button"
                onClick={() => {
                  hapticImpact('light')
                  setIsHelpOpen(true)
                }}
                className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors active:scale-[0.98]"
                aria-label={t('team.help.button', 'Что такое банды?')}
              >
                <HelpCircle className="w-5 h-5 text-text-secondary" />
              </button>
            </div>
            <p className="text-xs text-text-secondary">
              {t('team.bannerDescription', 'Играй в команде — вместе увеличивайте шансы на победу и получайте награды в общую казну')}
            </p>
          </div>
        </motion.div>

        {/* Своя банда или кнопка создания */}
        <motion.div variants={SECTION_VARIANT}>
          {myTeam ? (
            <button
              onClick={() => {
                playSound('select')
                navigate('/teams/my')
              }}
              className={`${CARD_CLASS} block w-full p-4 text-left transition-all active:scale-[0.98]`}
            >
              <div className="flex items-center gap-3">
                <TeamAvatar team={myTeam} size={48} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-text-secondary">{t('team.myTeam', 'Моя банда')}</p>
                  <p className="truncate font-bold text-white">{myTeam.name}</p>
                  {(myTeam.capture?.length > 0 || myTeam.top_places?.length > 0) && <p className="text-[11px] text-team-accent">{t('team.capture.myHonors', { owned: myTeam.capture?.length || 0, top: myTeam.top_places?.length || 0 })}</p>}
                </div>
                {user?.team_capture?.threat?.length > 0 && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" title={t('team.capture.threat')} />}
              </div>
            </button>
          ) : (
            <button
              onClick={handleCreateClick}
              className={`gradient-border-cta animate-gradient-shift flex w-full items-center justify-center gap-2 rounded-xl p-4 font-bold text-dark-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_25px_50px_-12px_rgba(0,255,127,0.3)] transition-all active:scale-[0.98] ${canCreate ? '' : 'opacity-60'}`}
            >
              <Plus size={20} />
              {t('team.createTitle', 'Создать банду')}
            </button>
          )}
        </motion.div>

        {top?.enabled && (
          <motion.div variants={SECTION_VARIANT}>
            <button
              onClick={() => {
                playSound('select')
                hapticImpact('light')
                navigate('/teams/top')
              }}
              className={`${CARD_CLASS} flex w-full items-center gap-3 p-4 text-left transition-all active:scale-[0.98]`}
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-yellow-400/30 bg-yellow-400/10 text-yellow-300">
                <Trophy size={22} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-bold text-white">{t('team.top.open', 'Топ банд за сегодня')}</span>
                <span className="block truncate text-xs text-text-secondary">{t('team.top.openHint', 'Рейтинг и ежедневные призы по каждому режиму')}</span>
              </span>
              <ChevronRight size={18} className="shrink-0 text-text-secondary" />
            </button>
          </motion.div>
        )}

        {capture?.enabled && !captureError && <motion.div variants={SECTION_VARIANT}>
          <button type="button" onClick={() => { playSound('select'); hapticImpact('light'); navigate('/teams/capture') }} className={`${CARD_CLASS} flex w-full items-center gap-3 p-4 text-left active:scale-[0.98]`}>
            <span className="rounded-xl border border-team-accent/30 bg-team-accent/10 p-2.5 text-team-accent"><ShieldAlert size={22} /></span>
            <span className="min-w-0 flex-1"><span className="block font-bold text-white">{t('team.capture.title')}</span><span className="block text-xs text-text-secondary">{t('team.capture.openHint')}</span></span>
            {user?.team_capture?.threat?.length > 0 ? <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" title={t('team.capture.threat')} /> : user?.team_capture?.owned?.length > 0 && <span className="rounded-full bg-team-accent/20 px-2 py-0.5 text-xs font-bold text-team-accent">{user.team_capture.owned.length}</span>}
            <ChevronRight size={18} className="text-text-secondary" />
          </button>
        </motion.div>}

        {/* Мои заявки — только пока игрок не в банде */}
        {!myTeam && (
          <motion.div variants={SECTION_VARIANT}>
            <MyApplicationsList />
          </motion.div>
        )}

        <motion.div variants={SECTION_VARIANT}><RecentLeftTeams /></motion.div>
        </div>

        <div className="min-w-0 space-y-5">
        {/* Открытые банды: подзаголовок виден только без поиска — в выдаче есть и ненабирающие */}
        <motion.div variants={SECTION_VARIANT} className="pt-3 lg:pt-0">
          {!query && (
            <h2 className="mb-3 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
              {t('team.openTeams', 'Открытые банды')}
            </h2>
          )}
          <div className="flex gap-2">
            <div className="relative min-w-0 flex-1">
              <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-secondary" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('team.searchPlaceholder', 'Поиск по названию')}
                className="w-full rounded-xl border border-white/10 bg-surface py-3 pl-10 pr-4 text-sm text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                hapticImpact('light')
                loadCatalog({ q: query, page, ...filters })
              }}
              disabled={isCatalogLoading}
              className="flex w-[46px] shrink-0 items-center justify-center rounded-xl border border-white/10 bg-surface text-text-secondary transition-all hover:text-white active:scale-[0.98] disabled:opacity-50"
              aria-label={t('team.catalogRefresh', 'Обновить каталог')}
              title={t('team.catalogRefresh', 'Обновить каталог')}
            >
              <RefreshCw size={18} className={isCatalogLoading ? 'animate-spin text-accent-primary' : ''} />
            </button>
          </div>
          {search.trim().length === 1 && (
            <p className="mt-1.5 text-xs text-text-secondary">
              {t('team.searchHint', 'Введи минимум 2 символа')}
            </p>
          )}
        </motion.div>

        <motion.div variants={SECTION_VARIANT}>
          <TeamCatalogFilters filters={filters} onChange={changeFilters} />
        </motion.div>

        {/* Каталог */}
        <motion.div variants={SECTION_VARIANT}>
          {isCatalogLoading && catalog.teams.length === 0 ? (
            <div className="grid gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`${CARD_CLASS} h-[136px] animate-pulse p-4`} />
              ))}
            </div>
          ) : catalogError ? (
            <div className={`${CARD_CLASS} p-8 text-center`}>
              <p className="mb-3 text-sm text-text-secondary">
                {t('team.catalogError', 'Не удалось загрузить каталог')}
              </p>
              <button
                onClick={() => loadCatalog({ q: query, page, ...filters })}
                className="rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-5 py-2.5 text-sm font-bold text-dark-primary transition-all active:scale-[0.98]"
              >
                {t('publicProfile.retry', 'Повторить')}
              </button>
            </div>
          ) : catalog.teams.length === 0 ? (
            <div className={`${CARD_CLASS} flex flex-col items-center p-8 text-center`}>
              <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
                <SearchX size={28} className="text-text-secondary" />
              </span>
              <p className="text-sm text-text-secondary">
                {filters.activity === 'now'
                  ? t('team.filters.emptyNow', 'Сейчас никто не играет вместе — посмотри, кто играл сегодня')
                  : query
                    ? t('team.searchEmpty', 'Банды с таким названием не нашлись')
                    : t('team.catalogEmpty', 'Пока никто не набирает участников')}
              </p>
              {/* Совместные игры бывают нечасто, поэтому «прямо сейчас» часто пусто */}
              {filters.activity === 'now' && (
                <button
                  onClick={() => changeFilters({ activity: 'today' })}
                  className="mt-3 rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-5 py-2.5 text-sm font-semibold text-accent-primary transition-all active:scale-[0.98]"
                >
                  {t('team.filters.showToday', 'Кто играл сегодня')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-3">
              {catalog.teams.map((team) => (
                <TeamCatalogCard
                  key={team.id}
                  team={team}
                  canApply={!myTeam}
                  hasPendingApplication={pendingTeamIds.has(team.id)}
                  onApply={isGuest ? openLogin : setApplyTeam}
                  onOpen={handleOpenTeam}
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Пагинация — по 20 банд на страницу */}
        {catalog.pages > 1 && (
          <motion.div variants={SECTION_VARIANT} className="flex items-center justify-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isCatalogLoading}
              className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
              aria-label={t('common.back', 'Назад')}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm tabular-nums text-text-secondary">
              {isCatalogLoading
                ? <Loader2 size={16} className="animate-spin text-accent-primary" />
                : `${catalog.page} / ${catalog.pages}`}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(catalog.pages, p + 1))}
              disabled={page >= catalog.pages || isCatalogLoading}
              className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
              aria-label={t('team.nextPage', 'Дальше')}
            >
              <ChevronRight size={18} />
            </button>
          </motion.div>
        )}
        </div>
      </motion.div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('team.help.title', 'Что такое банды?')}
        steps={[
          t('team.help.step1', 'Банда — команда игроков со своим профилем, своим оформлением и общей казной.'),
          t('team.help.step2', 'В Королевской битве ставка возвращается, если победил твой тиммейт. В кейс-баттлах выигрыш поровну делится между участниками победившей банды.'),
          t('team.help.step3', 'В других режимах играйте одновременно с тиммейтами, чтобы получать XP банды. Активность учитывается отдельно в каждом режиме: самые активные банды получат кристаллы в ежедневном розыгрыше.'),
          t('team.help.step4', 'Администрация банды решает, как распорядиться казной, и может распределять средства между участниками.'),
          t('team.help.step5', 'Банды захватывают режимы на 15 минут: играйте вместе, набирайте энергию и получайте приз в казну за удержание режима.')
        ]}
        highlightLastStep={false}
      />

      <TeamCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          setIsCreateOpen(false)
          navigate('/teams/my')
        }}
      />

      <TeamApplyModal
        team={applyTeam}
        isOpen={applyTeam != null}
        onClose={() => setApplyTeam(null)}
        onApplied={() => {
          setApplyTeam(null)
          loadMyApplications()
        }}
      />
    </div>
  )
}

export default Teams
